import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Super Admin Sandbox Management page.
 * - Lists all 3 sandbox slots with status.
 * - Configure form: property name, tax type + rate, optional logo upload,
 *   generates 9 credentials for the prospect.
 * - Reset button per active slot (confirmation stub; Phase 2 will polish
 *   into a modal dialog).
 */
export default function SandboxManagementPage() {
  const { token } = useAuth();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeForm, setActiveForm] = useState(null); // slot being configured
  const [form, setForm] = useState({ property_name: "", tax_type: "VAT", tax_rate: 12.5, logo_file: null });
  const [submitting, setSubmitting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(null); // slot pending reset confirmation

  const authHeader = () => ({ Authorization: `Bearer ${token}` });

  const fetchSlots = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API}/admin/sandboxes`, { headers: authHeader() });
      setSlots(r.data.slots || []);
    } catch (e) {
      toast.error("Failed to load sandbox slots");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSlots(); }, []);

  const openConfigure = (slot) => {
    setActiveForm(slot);
    setForm({ property_name: "", tax_type: "VAT", tax_rate: 12.5, logo_file: null });
  };

  const submitConfigure = async (e) => {
    e.preventDefault();
    if (!activeForm) return;
    setSubmitting(true);
    try {
      let logo_url = null;
      if (form.logo_file) {
        const fd = new FormData();
        fd.append("file", form.logo_file);
        const up = await axios.post(
          `${API}/admin/sandboxes/${activeForm}/upload-logo`,
          fd,
          { headers: { ...authHeader(), "Content-Type": "multipart/form-data" } }
        );
        logo_url = up.data.logo_url;
      }
      await axios.post(
        `${API}/admin/sandboxes/${activeForm}/configure`,
        {
          property_name: form.property_name,
          tax_type: form.tax_type,
          tax_rate: parseFloat(form.tax_rate),
          logo_url,
        },
        { headers: authHeader() }
      );
      toast.success(`Sandbox ${activeForm} configured`);
      setActiveForm(null);
      await fetchSlots();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Configure failed");
    } finally {
      setSubmitting(false);
    }
  };

  const resetSlot = async (slot) => {
    // Kicked off by the confirm dialog; caller sets confirmReset=null on completion
    try {
      await axios.post(`${API}/admin/sandboxes/${slot}/reset`, {}, { headers: authHeader() });
      toast.success(`Sandbox ${slot} reset`);
      await fetchSlots();
    } catch (e) {
      toast.error("Reset failed");
    } finally {
      setConfirmReset(null);
    }
  };

  const copy = (val) => {
    navigator.clipboard.writeText(val);
    toast.success("Copied");
  };

  const copyProspectPack = (slot) => {
    const s = slots.find((x) => x.slot === slot);
    if (!s || s.status !== "active") return;
    const url = `${window.location.origin}/sandbox/${slot}`;
    const expiresLocal = s.expires_at ? new Date(s.expires_at).toLocaleString() : "—";
    const lines = [
      `HERChain Solutions — Sandbox handoff`,
      ``,
      `Property:     ${s.property_name}`,
      `Sandbox URL:  ${url}`,
      `Expires:      ${expiresLocal}`,
      `Tax:          ${s.tax_type} ${s.tax_rate}%`,
      ``,
      `Nine role credentials (copy-paste into your email to the prospect):`,
      `============================================================`,
      ...(s.credentials || []).map(
        (c) => `${c.role_label.padEnd(24)}  ${c.email}  ${c.password}`
      ),
      `============================================================`,
      ``,
      `This is a private demo environment. All data will automatically`,
      `reset 72 hours after configuration. Powered by HERChain Solutions.`,
    ];
    navigator.clipboard.writeText(lines.join("\n"));
    toast.success("Prospect pack copied to clipboard");
  };

  const hoursRemaining = (expires_at) => {
    if (!expires_at) return null;
    const diff = new Date(expires_at).getTime() - Date.now();
    if (diff <= 0) return "expired";
    const h = Math.floor(diff / 3_600_000);
    const m = Math.floor((diff % 3_600_000) / 60_000);
    return `${h}h ${m}m`;
  };

  if (loading) {
    return <div className="p-8 text-slate-500" data-testid="sandbox-mgmt-loading">Loading sandbox slots…</div>;
  }

  return (
    <div className="p-8 max-w-6xl mx-auto" data-testid="sandbox-management-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800 mb-2">Sandbox Management</h1>
        <p className="text-slate-600">
          Configure up to three prospect sandboxes. Each sandbox is completely isolated with its
          own branding, credentials, and demo data. Sessions auto-reset after 72 hours.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {slots.map((s) => (
          <div
            key={s.slot}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6"
            data-testid={`sandbox-slot-${s.slot}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-400">Slot</p>
                <h2 className="text-xl font-bold text-slate-800">{s.slot}</h2>
              </div>
              <span
                className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${
                  s.status === "active"
                    ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
                    : "bg-slate-100 text-slate-500 border border-slate-300"
                }`}
                data-testid={`sandbox-status-${s.slot}`}
              >
                {s.status}
              </span>
            </div>

            {s.status === "available" ? (
              <div>
                <p className="text-sm text-slate-500 mb-4">
                  This slot is empty. Configure it to spin up a new prospect sandbox.
                </p>
                <button
                  onClick={() => openConfigure(s.slot)}
                  className="w-full bg-slate-800 hover:bg-slate-900 text-white py-2 rounded-md font-semibold"
                  data-testid={`sandbox-configure-${s.slot}-btn`}
                >
                  Configure
                </button>
              </div>
            ) : (
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-400">Property</p>
                  <p className="font-semibold text-slate-800" data-testid={`sandbox-property-${s.slot}`}>{s.property_name}</p>
                </div>
                <div className="flex gap-4">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-400">Tax</p>
                    <p className="text-slate-700">{s.tax_type} {s.tax_rate}%</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-400">Time left</p>
                    <p className="text-slate-700" data-testid={`sandbox-time-${s.slot}`}>{hoursRemaining(s.expires_at)}</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Sandbox URL</p>
                  <button
                    onClick={() => copy(`${window.location.origin}/sandbox/${s.slot}`)}
                    className="text-slate-700 hover:text-slate-900 underline text-xs break-all text-left"
                    data-testid={`sandbox-url-${s.slot}`}
                  >
                    {window.location.origin}/sandbox/{s.slot}
                  </button>
                </div>
                <details className="border-t pt-3">
                  <summary className="cursor-pointer text-sm font-semibold text-slate-700" data-testid={`sandbox-creds-toggle-${s.slot}`}>
                    View {s.credentials?.length || 0} credentials
                  </summary>
                  <div className="mt-3 space-y-2">
                    {(s.credentials || []).map((c, idx) => (
                      <div key={idx} className="bg-slate-50 rounded p-2 text-xs">
                        <div className="font-semibold text-slate-700">{c.role_label}</div>
                        <div className="flex items-center gap-2 text-slate-600">
                          <button onClick={() => copy(c.email)} className="hover:underline text-left flex-1 truncate" title="Copy email">
                            {c.email}
                          </button>
                        </div>
                        <div className="flex items-center gap-2 text-slate-600">
                          <button onClick={() => copy(c.password)} className="hover:underline text-left flex-1 font-mono truncate" title="Copy password">
                            {c.password}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
                <button
                  onClick={() => copyProspectPack(s.slot)}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-2 rounded-md font-semibold mt-3"
                  data-testid={`sandbox-copy-pack-${s.slot}-btn`}
                >
                  Copy prospect pack
                </button>
                <button
                  onClick={() => setConfirmReset(s.slot)}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded-md font-semibold mt-2"
                  data-testid={`sandbox-reset-${s.slot}-btn`}
                >
                  Reset sandbox
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Reset confirmation dialog */}
      {confirmReset && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="sandbox-reset-dialog"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-slate-800 mb-2">Reset sandbox — {confirmReset}?</h2>
            <p className="text-sm text-slate-600 mb-6">
              This will permanently wipe all data in the <strong>{confirmReset}</strong> sandbox —
              the prospect&apos;s property name, logo, credentials, vendors, purchase orders, items,
              stock alerts, inventory counts, and all activity. The slot will return to
              <span className="font-mono"> available</span>. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmReset(null)}
                className="flex-1 border border-slate-300 text-slate-700 py-2 rounded-md hover:bg-slate-50"
                data-testid="sandbox-reset-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() => resetSlot(confirmReset)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 rounded-md font-semibold"
                data-testid="sandbox-reset-confirm"
              >
                Yes, reset
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Configure modal */}
      {activeForm && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          data-testid="sandbox-configure-modal"
        >
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-slate-800 mb-1">Configure sandbox — {activeForm}</h2>
            <p className="text-sm text-slate-500 mb-6">Nine role credentials will be generated automatically.</p>
            <form onSubmit={submitConfigure} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Property name</label>
                <input
                  type="text"
                  value={form.property_name}
                  onChange={(e) => setForm({ ...form, property_name: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md"
                  placeholder="e.g. Bay Gardens Resorts"
                  required
                  data-testid="sandbox-form-property-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tax type</label>
                  <select
                    value={form.tax_type}
                    onChange={(e) => setForm({ ...form, tax_type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md"
                    data-testid="sandbox-form-tax-type"
                  >
                    <option value="VAT">VAT (St. Lucia)</option>
                    <option value="ABST">ABST (Antigua)</option>
                    <option value="GCT">GCT (Jamaica)</option>
                    <option value="None">None</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Tax rate %</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={form.tax_rate}
                    onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md"
                    required
                    data-testid="sandbox-form-tax-rate"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Property logo (optional)</label>
                <input
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp"
                  onChange={(e) => setForm({ ...form, logo_file: e.target.files?.[0] || null })}
                  className="w-full text-sm"
                  data-testid="sandbox-form-logo"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setActiveForm(null)}
                  className="flex-1 border border-slate-300 text-slate-700 py-2 rounded-md hover:bg-slate-50"
                  data-testid="sandbox-form-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-slate-800 hover:bg-slate-900 text-white py-2 rounded-md font-semibold disabled:opacity-50"
                  data-testid="sandbox-form-submit"
                >
                  {submitting ? "Configuring…" : "Configure sandbox"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
